import type { DTOHashtagInfo } from './DTOHashtagInfo';
import type { DTOInterestInfo } from './DTOInterestInfo';
import type { DTOLightweightGiftPointsInfo } from './DTOLightweightGiftPointsInfo';
import type { DTOLocation } from './DTOLocation';
import type { DTOProfileCardAsyncInfo } from './DTOProfileCardAsyncInfo';
import type { DTOProfileImageInfo } from './DTOProfileImageInfo';
import type { DTOProfileReactionInfo } from './DTOProfileReactionInfo';
import type { DTOQualityUserBadge } from './DTOQualityUserBadge';
import type { DTOUserLanguageEntry } from './DTOUserLanguageEntry';

export const DTO_PROFILE_CARD_TYPE_TYPES = {
  /**
   * 내 자신의 프로필을 의미하는 상수.
   *
   * 미러뷰와 친구탭에서 진입하는 유저 본인의 프로필 카드를 뜻한다.
   *
   * {@code id} 값으로 User ID {@link LoginResponse#getUserId()}를 사용한다.
   */
  MY_PROFILE: 'MY_PROFILE',

  /**
   * @deprecated
   * 지난 대화 상대 화면에서 매칭 했던 상대의 프로필을 의미하는 상수.
   *
   * 지난 대화 상대 탭의 리스트 정보는 클라이언트 자체 구성이지만, 리스트의 각 유저에 대한 세부 정보는 이 타입의 프로필 카드를 이용한다.
   *
   * {@code id} 값으로 Match ID {@link MatchInfo#getMatchId()} 를 사용한다.
   */
  HISTORY: 'HISTORY',

  /**
   * 매치 진행 중 상대 프로필을 의미하는 상수.
   *
   * 매치 연결 중 유저가 상대방 프로필을 클릭해 확인할 수 있는 세부 정보를 담고 있는 프로필 카드를 뜻한다.
   *
   * {@code id} 값으로 Match ID {@link MatchInfo#getMatchId()} 를 사용한다.
   */
  MATCH: 'MATCH',

  /**
   * 친구 및 대화방 화면에서의 상대 유저의 프로필을 의미하는 상수.
   *
   * 친구 혹은 대화방 목록이 아니라(주의), 해당 유저의 세부 정보를 리스트 혹은 대화방에서의 진입을 통해 표시하는 프로필 카드를 뜻한다.
   *
   * @see FriendInfo#getProfileCard()
   */
  FRIEND: 'FRIEND',

  /**
   * 친구 및 대화방 화면에서 아자르 공식 계정의 프로필을 의미하는 상수.
   *
   * FRIEND 타입과 같은 곳에서 사용되지만, 공식 계정의 경우 기능상 다른 부분이 있어서 구분을 위해 분리한다.
   *
   * {@code id} 값으로 Friend ID {@link FriendInfo#getFriendId()} 를 사용한다.
   * 기존 Friend ID 와 같은 정책을 따르기 때문에 id 값은 음수가 제공된다.
   *
   * @see FriendInfo#getProfileCard()
   */
  OFFICIAL_ACCOUNT: 'OFFICIAL_ACCOUNT',

  /**
   * XO 화면에서 다른 유저의 프로필을 의미하는 상수.
   *
   * 현재의 XO 기능상 서버로부터 추천 받은 유저의 카드 프로필, 나에게 interest 를 보낸 유저의 카드 프로필을 뜻한다.
   * 주의 - Mutually Interested 유저의 경우 '친구'와 동등하기 때문에, FRIEND 타입이 사용된다.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   *
   * @see AsyncApiService#listRecommendedProfileCards(Integer)
   * @see AsyncApiService#listInterestedMeCardInfos(ListRequest)
   */
  PEER_ASYNC: 'PEER_ASYNC',

  /**
   * XO 화면에서 자신의 프로필을 의미하는 상수.
   *
   * 자신의 XO 카드 프로필을 뜻한다.
   * MY_PROFILE 타입의 진입점에서는 UI상 표시하지 않는 정보들도 있고, 다르게 가공되는 정보들이 있기 때문에 별도 타입을 이용해 표시한다.
   *
   * {@code id} 값으로 User ID를 사용한다. (자신의 Card ID 에 대한 Convention 이 없음)
   */
  MY_ASYNC: 'MY_ASYNC',

  /**
   * 라운지 `추천카드 화면에서` 다른 유저의 프로필을 의미하는 상수.
   * 자신의 프로필의 경우 {@link #TYPE_MY_ASYNC} 를 사용한다.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  LOUNGE_RECOMMEND: 'LOUNGE_RECOMMEND',

  /**
   * 라운지 `나를 좋아한 유저 카드 화면에서` 다른 유저의 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  LOUNGE_LIKE: 'LOUNGE_LIKE',

  /**
   * 라운지 `오늘의 추천 친구 카드 화면에서` 다른 유저의 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  TODAY_LOUNGE_RECOMMEND: 'LOUNGE_TODAY_RECOMMEND',

  /**
   * 라운지 `매칭 큐 대기 카드 화면에서` 다른 유저의 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  LOUNGE_MATCH_QUEUE: 'LOUNGE_MATCH_QUEUE',

  /**
   * 유저 히스토리 화면에서 상호작용 했던 상대의 프로필을 의미하는 상수.
   * 서버에서는 상대방 유저 아이디를 라운지와 카드와 동일한 규칙으로 가져올 수 있다.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  HISTORY_CARD: 'HISTORY_CARD',

  /**
   * 매치 대기중 ASYNC_CARD 팀매치에서 보여주는 추천 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  IN_MATCH_RECOMMEND: 'IN_MATCH_RECOMMEND',

  /**
   * Azar Pick 에서 보여주는 추천 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  AZAR_PICK: 'AZAR_PICK',

  /**
   * 신규 Lounge 의 ForYou 영역에서 보여주는 추천 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  PICK_LOUNGE_FOR_YOU: 'PICK_LOUNGE_FOR_YOU',

  /**
   * 신규 Lounge 의 ALL 영역에서 보여주는 추천 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  PICK_LOUNGE_ALL: 'PICK_LOUNGE_ALL',

  /**
   * 나를 조회한 사람들 화면에서 다른 유저의 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  PROFILE_VIEWER: 'PROFILE_VIEWER',

  /**
   * 나를 본 사람들(Viewed Me) 화면에서 다른 유저의 프로필을 의미하는 상수.
   *
   * {@code id} 값으로 Card ID를 사용한다.
   */
  PROFILE_VIEWER_CARD: 'PROFILE_VIEWER_CARD',

  /**
   * Invite from Swipe Azar 카드 타입.
   *
   * 스자르 유저가 카자르 유저에게 초대를 보낼 수 있는 카드를 뜻한다.
   * {@code id} 값으로 Card ID를 사용한다.
   */
  INVITE_FROM_SWIPE_AZAR: 'INVITE_FROM_SWIPE_AZAR',
} as const;

export type DTOProfileCardType =
  (typeof DTO_PROFILE_CARD_TYPE_TYPES)[keyof typeof DTO_PROFILE_CARD_TYPE_TYPES];

export const DTO_PROFILE_CARD_NICKNAME_REVIEW_STATE_TYPES = {
  /** 닉네임 리뷰가 진행 중인 상태. */
  PENDING: 'PENDING',

  /** 닉네임 리뷰가 거절된 상태. */
  REJECTED: 'REJECTED',
} as const;

export type DTOProfileCardNicknameReviewState =
  (typeof DTO_PROFILE_CARD_NICKNAME_REVIEW_STATE_TYPES)[keyof typeof DTO_PROFILE_CARD_NICKNAME_REVIEW_STATE_TYPES];

export const DTO_PROFILE_CARD_GENDER_TYPES = {
  /** 남자 */
  MALE: 'MALE',
  /** 여자 */
  FEMALE: 'FEMALE',
  /** 제3의 성별 */
  NONBINARY: 'NONBINARY',
  /** 성별 숨김 */
  HIDE: '',
} as const;

export type DTOProfileCardGender =
  (typeof DTO_PROFILE_CARD_GENDER_TYPES)[keyof typeof DTO_PROFILE_CARD_GENDER_TYPES];

export const DTO_PROFILE_CARD_FOLLOW_STATE_TYPES = {
  REQUESTED_BY_PEER: 'REQUESTED_BY_PEER',
  IGNORED_BY_USER: 'IGNORED_BY_USER',
  REQUESTED_BY_USER: 'REQUESTED_BY_USER',
  ACCEPTED: 'ACCEPTED',
} as const;

export type DTOProfileCardFollowState =
  (typeof DTO_PROFILE_CARD_FOLLOW_STATE_TYPES)[keyof typeof DTO_PROFILE_CARD_FOLLOW_STATE_TYPES];

export interface DTOProfileCard {
  /**
   * 이 프로필 카드의 타입.
   * 이 프로필 카드가 어떤 경로를 통해 표시되는지, 어떤 종류의 유저 정보를 담고있는지에 따라 달라진다.
   * 클라이언트에서 특정 정보나 기능 노출 여부가 조건에 따라 달라질 때 이 타입을 기반으로 판단 할 수 있도록 한다.
   * - 반면 UI 적인 on-off 여부가 아닌 필드 값에 대한 가공이 필요한 경우 모두 서버에서 해서 내려준다.
   */
  type: DTOProfileCardType;

  /**
   * 프로필 카드의 고유 ID.
   * 맥락(type) 에 따라 다른 DTO 에서 사용되고 있는 User ID / Card ID / Match ID / Friend ID 와 동일한 값을 사용한다.
   */
  id: string;

  /**
   * 카드에 표시될 유저의 이름.
   * 서버에서 정책 및 유저 설정에 따라 적절히 가공한 username 을 제공한다.
   */
  nickname: string;

  /** 유저의 현재 닉네임 리뷰 상태 */
  nicknameReviewState?: DTOProfileCardNicknameReviewState;

  /**
   * 아자르 ID.
   * 본인의 프로필에만 표시하기 때문에 {@link #TYPE_MY_PROFILE} 에서만 의미가 있다.
   **/
  azarId?: string;

  /**
   * 유저의 성별 정보('MALE' = 남자, 'FEMALE' = 여자, 'NONBINARY' = 제3의 성별).
   *
   * 1. 성별 노출 설정
   * - display gender 설정: 유저가 직접 다른 유저에게 성별을 공개할지 여부를 설정할 수 있음
   * - 국가 별 non binary 지원 여부: non binary 성별을 지원하는 국가 목록이 정해져 있음
   *
   * 2. 성별 노출 정책
   * 본인 프로필
   * - 위 설정과 상관 없이 항상 노출함
   *
   * 상대 프로필
   * - 상대 유저의 display gender 설정이 false 인 경우 노출하지 않음
   * - 상대 유저의 성별이 non binary 이고 유저 본인 국가가 non binary 미지원 국가이면 노출하지 않음
   * - 그 외의 경우에는 성별 노출
   *
   * 서버에서 위와 같은 기준으로 노출 여부를 판단하여 노출하지 않아야 하는 경우에는 empty string 으로 내려주므로,
   * 클라이언트는 gender 가 empty string 이 아닌 경우에 성별을 노출하면 된다.
   */
  gender: DTOProfileCardGender;

  /** 프로필 이미지 목록 */
  profileImageInfos: DTOProfileImageInfo[];

  /** 사용자의 Cover 프로필 URL */
  coverProfileImageUrl?: string;

  /** 사용자의 프로필 Bio */
  profileBio?: string;

  /**
   * @deprecated 사용자가 선택한 Interest 목록
   */
  interestInfos?: DTOInterestInfo[];

  /**
   * @deprecated 유저가 선택한 사용 가능한 언어 목록
   */
  userLanguageEntries?: DTOUserLanguageEntry[];

  /** 유저의 현재 위치 */
  location?: DTOLocation;

  /**
   * @deprecated 유저가 받은 쿨포인트 (엄지척 점수)
   */
  coolPoint: number;

  /** 유저가 받은 LightweightGift point 정보 */
  lightweightGiftPointsInfo?: DTOLightweightGiftPointsInfo;

  /** 프로필 카드의 Async 관련 부가 정보 */
  asyncInfo?: DTOProfileCardAsyncInfo;

  /**
   * 유저가 접속중인지 여부
   * 유저가 마지막 API 콜을 한지 10분 이내인 경우 이 값을 true 로 내려준다.
   * 이 시간 판정은 서버에서 설정으로 바뀔 수 있다
   *
   * xo, 친구 타입에서는 ProfileCardAsyncInfo 의 online 을 보지않고 이 필드를 참조한다.
   * xo, 친구 를 제외한 타입에서는 null 로 내려간다.
   *
   * @since 1580
   */
  online?: boolean;

  /** 유저의 해시태그 정보 */
  hashtagInfos?: DTOHashtagInfo[];

  /**
   * @deprecated 유저의 reputation score
   */
  reputationScore?: number;

  /** 유저를 식별할 수 있는 20자 내의 고유한 아이디 값 */
  userCode?: string;

  /** 유저의 리액션 정보 */
  profileReactionInfos: DTOProfileReactionInfo[];

  /** 유저의 reputation rating (소수점 형태지만 string으로 저장됨) */
  reputationRating?: string;

  /** 해당 카드가 AI Bot 인지 여부 */
  aiBot?: boolean;

  /** 유저가 선택한 선호 언어 목록 */
  userPreferredLanguageEntries?: DTOUserLanguageEntry[];

  /** Non binary 성별 유저의 child gender 번역 값 */
  childGenderText?: string;

  /**
   * Follow 상태값 (기존 친구 상태와 동일)
   * 프로필 팝업에서 Follow 버튼의 상태를 결정하기 위해 사용함.
   *
   * 상대방과의 관계가 없는 경우 null.
   * 조회하는 UI에 따라서, UI 정책에 맞춰서 안쓰거나 성능상 조회를 안하면 null로 내려준다.
   * 또는 조회 이슈가 있는 경우 null로 내려준다.
   *
   * @since 2490
   */
  followState?: DTOProfileCardFollowState;

  /** 유저의 퀄리티 뱃지 정보 */
  qualityUserBadge?: DTOQualityUserBadge;
}
